import type { LogLevel } from '@core/logging/log-level'
import type { PushSDKLifecycleCallbacks } from './callbacks/push-sdk-lifecycle-callbacks'
import type { PermissionLifecycleCallbacks } from './callbacks/permission-lifecycle-callbacks'
import type { PushSdkConfig, PushSdkConsentState } from './types/push-sdk-config'
import type { UserProfile } from './modules/user-profile'
import type { PushNotifications } from './modules/push-notifications.ts'
import type { EComm } from './modules/ecomm.ts'
import type { PromptLifecycleCallbacks } from './callbacks/prompt-lifecycle-callbacks.ts'
import {
    ECommEventRecord,
    ECommEventRecordWithRating,
    ECommEventRecordWithoutQuantity,
    ECommProductRecord,
    ECommProductRecordWithRating,
    ECommProductRecordWithoutQuantity,
    ECommPurchaseDetails,
    ECommPurchaseRecord,
    ExternalIdCommandValue,
    LoadCommandValue,
    PageTagVisitCommandValue,
    ProfileAppendCommandValue,
    ProfileCommandValue,
    ProfileRemoveCommandValue,
    PushSdkCommand,
    PushSdkContext,
    PushSdkEvent,
    PushSdkEventCallback,
    PushSdkUser,
    ShowPromptCommandValue,
} from './types/push-sdk-legacy'
import { PushSdkPublicImpl } from './push-sdk-public-impl.ts'

/** @public */
export type PushSDK = {
    // region Initializers

    /**
     * Initializes the PushlySDK for the specified domainKey.
     *
     * @example
     * ```typescript
     * PushlySDK.setConfiguration({
     *   domainKey: 'REPLACE_WITH_SDK_KEY'
     * })
     * ```
     */
    setConfiguration(config: PushSdkConfig): void

    getConfiguration(): PushSdkConfig | null

    // endregion
    // region Lifecycle Registrations

    /**
     *
     * @example
     * ```typescript
     * import { SubscriberStatus } from '@pushly/push-sdk-web`
     *
     * PushlySDK.registerPushSDKLifecycleCallbacks({
     *   onPushSDKDidFinishLoading: (domain, subscriberStatus) => {
     *     // Add code to execute after SDK finishes loading
     *   }
     * })
     * ```
     */
    registerPushSDKLifecycleCallbacks(callbacks: PushSDKLifecycleCallbacks): void

    /**
     *
     * @example
     * ```typescript
     * import { PermissionResponse } from '@pushly/push-sdk-web`
     *
     * PushlySDK.registerPermissionLifecycleCallbacks({
     *   onPushSDKDidReceivePermissionResponse: (response) => {
     *     // Add code to handle custom logic based on user permission response
     *   }
     * })
     * ```
     */
    registerPermissionLifecycleCallbacks(callbacks: PermissionLifecycleCallbacks): void

    /**
     *
     * @example
     * ```typescript
     * import { PermissionResponse } from '@pushly/push-sdk-web`
     *
     * PushlySDK.registerPromptLifecycleCallbacks({
     *   onPushSDKUserIsIneligibleToPrompt: () => {
     *     // Add code to execute when user is not eligible to be prompted
     *   }
     * })
     * ```
     */
    registerPromptLifecycleCallbacks(callbacks: PromptLifecycleCallbacks): void

    // endregion
    // region Logging

    logLevel: LogLevel

    /**
     * Call to display user details panel
     */
    debug(): Promise<void>
    debug(display: boolean): Promise<void>

    // endregion
    // region Properties

    /** Instance ID of the PushlySDK */
    readonly iid: string

    /** Current version of the PushlySDK */
    readonly version: string

    /** Loaded state of the PushlySDK */
    readonly isLoaded: boolean

    // endregion
    // region Namespaces

    readonly UserProfile: UserProfile
    /** @deprecated Use `PushlySDK.UserProfile` catalog methods instead. */
    readonly EComm: EComm
    readonly PushNotifications: PushNotifications

    // endregion
    // region Legacy
    // region PushlySDK.load

    /**
     * @deprecated Use `PushlySDK.setConfiguration(PushSdkConfig)`
     */
    load(config: PushSdkConfig): void

    // endregion
    // region PushlySDK.requestNotificationPermission

    /**
     * @deprecated Use: `PushlySDK.PushNotifications.showPermissionPrompt(promptId)`
     */
    requestNotificationPermission(promptId: number): Promise<void>
    /**
     * @deprecated Use: `PushlySDK.PushNotifications.showPermissionPrompt()`
     */
    requestNotificationPermission(): Promise<void>

    // endregion
    // region PushlySDK.on | PushlySDK.trigger

    /**
     * @deprecated Use appropriate lifecycle callbacks.
     * @see PushSDKLifecycleCallbacks
     * @see PromptLifecycleCallbacks
     * @see PermissionLifecycleCallbacks
     */
    on(event: PushSdkEvent, cb: PushSdkEventCallback): void

    /**
     * @deprecated Use appropriate lifecycle callbacks.
     * @see PushSDKLifecycleCallbacks
     * @see PromptLifecycleCallbacks
     * @see PermissionLifecycleCallbacks
     */
    watch(event: PushSdkEvent, cb: PushSdkEventCallback): void

    /** @deprecated */
    watchOnce(event: PushSdkEvent, cb: PushSdkEventCallback): void

    /** @deprecated */
    trigger(event: string, data: any): void

    // endregion
    // region PushlySDK.push

    // Generic Commands

    /** @deprecated Use `PushlySDK.setConfiguration(pushSdkConfig)` */
    push(options: [command: PushSdkCommand.LOAD, options: LoadCommandValue]): void

    /** @deprecated Use `PushlySDK.UserProfile.requestUserDeletion()` */
    push(options: [command: PushSdkCommand.REQUEST_USER_DELETION]): void

    /** Same as `PushlySDK.UserProfile.optOut()`, and safe to queue before the bundle loads. */
    push(options: [command: PushSdkCommand.OPT_OUT]): void

    /** Same as `PushlySDK.UserProfile.optIn()`, and safe to queue before the bundle loads. */
    push(options: [command: PushSdkCommand.OPT_IN]): void

    /** Same as `PushlySDK.UserProfile.setConsent(state)`, and safe to queue before the bundle loads. */
    push(options: [command: PushSdkCommand.CONSENT, state: PushSdkConsentState]): void

    // PushlySDK.UserProfile commands

    /** @deprecated Use `PushlySDK.UserProfile.setExternalId(externalId)` */
    push(options: [command: PushSdkCommand.EXTERNAL_ID, options: ExternalIdCommandValue]): void

    /** @deprecated Use `PushlySDK.UserProfile.unsetExternalId()` */
    push(options: [command: PushSdkCommand.DEREGISTER_EXTERNAL_ID]): void

    /** @deprecated Use `PushlySDK.UserProfile.set(profileUpdates)` */
    push(options: [command: PushSdkCommand.PROFILE, options: ProfileCommandValue]): void

    /** @deprecated Use `PushlySDK.UserProfile.append(key, values)` */
    push(options: [command: PushSdkCommand.PROFILE_APPEND, options: ProfileAppendCommandValue]): void

    /** @deprecated Use `PushlySDK.UserProfile.remove(key, values)` */
    push(options: [command: PushSdkCommand.PROFILE_REMOVE, options: ProfileRemoveCommandValue]): void

    /** @deprecated Use `PushlySDK.UserProfile.trackActivity(uri, pageTags)` */
    push(options: [command: PushSdkCommand.PAGE_TAG_VISIT, PageTagVisitCommandValue]): void

    // PushlySDK.PushNotifications commands

    /** @deprecated Use `PushlySDK.PushNotifications.showPermissionPrompt()` */
    push(options: [command: PushSdkCommand.SHOW_PROMPT]): void

    /** @deprecated Use `PushlySDK.PushNotifications.showPermissionPrompt(promptId, showPromptOptions)` */
    push(options: [command: PushSdkCommand.SHOW_PROMPT, options: ShowPromptCommandValue]): void

    // PushlySDK.EComm commands

    /** @deprecated Use `PushlySDK.EComm.trackSavedItems(items)` */
    push(
        options: [
            command: PushSdkCommand.SAVE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void

    /** @deprecated Use `PushlySDK.EComm.unsaveItems(items)` */
    push(
        options: [
            command: PushSdkCommand.UNSAVE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void

    /** @deprecated Use `PushlySDK.EComm.trackViewedItems(items)` */
    push(
        options: [
            command: PushSdkCommand.VIEW_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void

    /** @deprecated Use `PushlySDK.EComm.trackCompletedItems(items)` */
    push(
        options: [
            command: PushSdkCommand.COMPLETE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void

    /** @deprecated Use `PushlySDK.EComm.uncompleteItems(items)` */
    push(
        options: [
            command: PushSdkCommand.UNCOMPLETE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void

    /** @deprecated Use `PushlySDK.EComm.trackRatedItems(items)` */
    push(options: [command: PushSdkCommand.RATE_ITEM, ECommProductRecordWithRating | ECommEventRecordWithRating]): void

    /** @deprecated Use `PushlySDK.EComm.unrateItems(items)` */
    push(
        options: [
            command: PushSdkCommand.UNRATE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void

    /** @deprecated Use `PushlySDK.EComm.addToCart(items)` */
    push(options: [command: PushSdkCommand.ADD_TO_CART, ECommProductRecord | ECommEventRecord]): void

    /** @deprecated Use `PushlySDK.EComm.updateCart(items)` */
    push(options: [command: PushSdkCommand.UPDATE_CART, ECommProductRecord | ECommEventRecord]): void

    /** @deprecated Use `PushlySDK.EComm.purchase()` */
    push(options: [command: PushSdkCommand.PURCHASE]): void

    /** @deprecated Use `PushlySDK.EComm.purchase(items, purchaseId, priceValue)` */
    push(options: [command: PushSdkCommand.PURCHASE, ECommPurchaseRecord]): void

    /** @deprecated */
    push(options: [command: PushSdkCommand.PURCHASE, ECommPurchaseDetails]): void

    // Events

    /**
     * @deprecated Use appropriate lifecycle callbacks.
     * @see PushSDKLifecycleCallbacks
     * @see PromptLifecycleCallbacks
     * @see PermissionLifecycleCallbacks
     */
    push(options: [command: `on_${PushSdkEvent}`, PushSdkEventCallback]): void

    // endregion
    // region PushlySDK.showLogs | PushlySDK.getLogs

    /** @deprecated Use `PushlySDK.logLevel = desiredLogLevel` */
    showLogs(show: boolean): void

    /** @deprecated Use `PushlySDK.logLevel = LogLevel.INFO` */
    showLogs(): void

    /** @deprecated */
    getLogs(): any[]

    // endregion
    // region Helpers

    /** @deprecated */
    context: PushSdkContext

    /** @deprecated */
    getUser(): PushSdkUser

    /** @deprecated Use `PushlySDK.PushNotifications.getIsPaused()` */
    isUserSoftUnsubscribed(): Promise<boolean>

    /** @deprecated Use `PushlySDK.PushNotifications.getIsEligibleToPrompt()` */
    isUserEligibleToPrompt(): Promise<boolean>

    /** @deprecated Use `PushlySDK.PushNotifications.getIsSubscribed()` */
    isUserSubscribed(): Promise<boolean>

    // endregion
    // endregion
}

export const PushSDK: PushSDK = PushSdkPublicImpl.getInstance()
